import React from "react";

/**
 * Render-time error capture for the headless export page.
 *
 * During a Puppeteer render, a thrown React error crashes the Chromium renderer
 * process, which takes the whole page down before console output reliably
 * flushes to Node — so the server only ever saw a generic
 * `Page.captureScreenshot` ProtocolError with no cause. This module surfaces the
 * actual browser-side error to the Node render logs by:
 *
 *   1. An ErrorBoundary that catches render/lifecycle exceptions in the tree and
 *      reports the error + React component stack.
 *   2. Global window.onerror / unhandledrejection handlers for everything a
 *      boundary can't catch (event handlers, async code, non-React scripts).
 *
 * Reports are sent with navigator.sendBeacon, which the browser flushes even as
 * the page is being torn down — the key property for catching an error that is
 * about to kill the renderer. Falls back to a keepalive fetch if sendBeacon is
 * unavailable. The target is the frame-proxy's /clientlog endpoint, reachable at
 * window.picmaker_proxyPort (already injected into every render tab).
 *
 * Entirely inert outside a render: if there's no proxy port, reporting no-ops,
 * so this never affects the normal app.
 */

function proxyBase() {
  if (typeof window === "undefined") return null;
  const port = window.picmaker_proxyPort;
  return port ? `http://127.0.0.1:${port}` : null;
}

let reportSeq = 0;

export function reportRenderError(payload) {
  try {
    const base = proxyBase();
    if (!base) return; // not a render context — do nothing

    const body = JSON.stringify({
      ...payload,
      seq: ++reportSeq,
      url: typeof window !== "undefined" ? window.location.href : null,
      frame:
        typeof window !== "undefined" && typeof window.picmaker_frameCounter === "number"
          ? window.picmaker_frameCounter
          : null,
      ts: Date.now(),
    });

    const url = `${base}/clientlog`;

    // sendBeacon survives page teardown; this is why it's preferred here.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    // Fallback — keepalive lets the request outlive the document.
    if (typeof fetch !== "undefined") {
      fetch(url, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {});
    }
  } catch (_) {
    // Reporting must never itself throw and mask the original error.
  }
}

// ─── Global handlers (installed once) ─────────────────────────────────────────
let globalInstalled = false;
export function installGlobalRenderErrorHandlers() {
  if (globalInstalled || typeof window === "undefined") return;
  globalInstalled = true;

  window.addEventListener("error", (event) => {
    reportRenderError({
      kind: "window.onerror",
      message: event.message || String(event.error || "unknown"),
      stack: event.error && event.error.stack ? event.error.stack : null,
      source: event.filename || null,
      line: event.lineno ?? null,
      col: event.colno ?? null,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    reportRenderError({
      kind: "unhandledrejection",
      message:
        reason && reason.message ? reason.message : String(reason ?? "unknown rejection"),
      stack: reason && reason.stack ? reason.stack : null,
    });
  });
}

// ─── Error boundary ───────────────────────────────────────────────────────────
export class RenderErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    reportRenderError({
      kind: "react-boundary",
      message: error && error.message ? error.message : String(error),
      stack: error && error.stack ? error.stack : null,
      componentStack: info && info.componentStack ? info.componentStack : null,
    });
    // Re-log to console too — harmless if it doesn't flush, useful if it does.
    // eslint-disable-next-line no-console
    console.error("[RenderErrorBoundary]", error, info && info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // Keep a frame renderable (black) rather than an unmounted tree, so the
      // renderer doesn't separately time out waiting on window.ready.
      return (
        <div style={{ width: "100vw", height: "100vh", background: "#000" }} />
      );
    }
    return this.props.children;
  }
}
